import React, { useState, useRef, useEffect } from "react";

type FeatureSection = { label: string; features: string[] };

type CollapsibleFeaturesListProps = { includedFeatures: FeatureSection[] };

function countFeatures(sections: FeatureSection[]): number {
    return sections.reduce((total, section) => total + section.features.length, 0);
}

const STAGGER_DELAY_MS = 30;

export function CollapsibleFeaturesList({ includedFeatures }: CollapsibleFeaturesListProps) {
    const [expanded, setExpanded] = useState(false);
    const [visibleCount, setVisibleCount] = useState(0);
    const contentRef = useRef<HTMLDivElement>(null);
    const [contentHeight, setContentHeight] = useState(0);

    const includedCount = countFeatures(includedFeatures);

    let flatIndex = 0;
    const indexedSections = includedFeatures.map((section) => ({
        ...section,
        features: section.features.map((f) => ({ name: f, index: flatIndex++ }))
    }));

    useEffect(() => {
        if (contentRef.current) {
            setContentHeight(contentRef.current.scrollHeight);
        }
    }, [expanded, visibleCount]);

    useEffect(() => {
        if (!expanded) {
            setVisibleCount(0);
            return;
        }

        let current = 0;
        const interval = setInterval(() => {
            current++;
            setVisibleCount(current);
            if (current >= includedCount) clearInterval(interval);
        }, STAGGER_DELAY_MS);

        return () => clearInterval(interval);
    }, [expanded, includedCount]);

    const toggle = () => {
        window?.eventTracker?.event({
            type: "Trial Modal Features Toggled",
            data: { expanded: !expanded, workspaceId: window.currentWorkspace }
        });
        setExpanded((prev) => !prev);
    };

    return (
        <div className="flex flex-col gap-3 text-sm">
            <button
                onClick={toggle}
                className="flex w-full cursor-pointer items-center gap-3 rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-left text-sm text-yellow-900 transition-colors hover:bg-yellow-100"
            >
                <i className="far fa-gift flex-shrink-0 text-yellow-600" />
                <span className="flex-1">
                    You now have access to{" "}
                    <span className="font-semibold">{includedCount} premium features</span>!{" "}
                    {expanded ? "" : "See what they are."}
                </span>
                <i
                    className={`far fa-chevron-down text-yellow-600 transition-transform duration-300 ${expanded ? "rotate-180" : ""}`}
                />
            </button>

            <div
                ref={contentRef}
                className="overflow-hidden transition-[max-height] duration-300 ease-in-out"
                style={{ maxHeight: expanded ? `${contentHeight}px` : "0px" }}
            >
                <div className="flex flex-col gap-3 pt-1">
                    <div className="flex items-center gap-4">
                        <h3 className="text-sm font-medium text-primary">Included features</h3>
                        <hr className="flex-grow border-gray-200" />
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-tertiary">
                        {indexedSections.map((section) => (
                            <div className="flex flex-col gap-1" key={section.label}>
                                <p className="font-medium text-secondary">{section.label}</p>
                                <ul className="space-y-1">
                                    {section.features.map(({ name, index }) => (
                                        <li
                                            key={name}
                                            className="flex items-baseline gap-1 transition-all duration-200"
                                            style={{
                                                opacity: index < visibleCount ? 1 : 0,
                                                transform:
                                                    index < visibleCount
                                                        ? "translateY(0)"
                                                        : "translateY(4px)"
                                            }}
                                        >
                                            <i className="far fa-fw fa-check text-green-500" />
                                            {name}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
